import ReactDOM from 'react-dom';
import React from 'react';
import { arraysEqual } from './helpers';

import { Filter } from './Filter';
import { measures } from './services/stats/calcStats';
import StatsPanel from './components/statsPanel/StatsPanel';

const unitTypes = {
  metric: {
    distance: 'km',
  },
  imperial: {
    distance: 'mi',
  },
};

const defaultStats = {
  units: 'metric',
  labels: unitTypes.metric,
  distance: {
    total: 1, // need to have a minimum length of 1 meter
    current: 0,
  },
  numberVisible: 1,
};

const getStat = (stat, point, points, units, state) => {
  const calcValue = measures[stat].calc(point, points, state);
  if (!measures[stat][units]) return calcValue;
  const measurement = measures[stat][units];
  return measurement.calc(calcValue);
};

const getUnits = (statName, state) => {
  if (statName === 'gmtTime' || statName === 'localTime') {
    return measures[statName].units(state);
  } else {
    return !measures[statName][state.stats.units]
      ? measures[statName].units
      : measures[statName][state.stats.units].units;
  }
};

export default class Stats {
  constructor(store) {
    this.panelProps = {
      toggleVisible: toggleFullStats.bind(null, store.dispatch),
    };
    // String list of all stats that are being filtered
    this.filteredStatsList = undefined;
    // Object that holds all of the filter instances
    this.statsFilters = undefined;
  }

  reCreateStatFilters(rawStatsPoint) {
    // Resets the stat filters
    this.filteredStatsList = [];
    this.statsFilters = {};
    // Updates them with the newest stats
    this.filteredStatsList = Object.keys(rawStatsPoint);
    this.filteredStatsList.forEach(statName => {
      // TODO Optionally Add custom convolutions from calcStats here
      this.statsFilters[statName] = new Filter(10, 10);
    });
  }

  update(store, tracks) {
    const state = store.getState();
    const stats = [];

    // Compute stats for the active track
    if (state.tracks.activeTrack) {
      const track = tracks.getTrack(state.tracks.activeTrack);
      const statsList = state.activities[state.tracks.activeTrack].stats;

      const rawStatsPoint = {};
      statsList.forEach(s => {
        rawStatsPoint[measures[s].channel] = 0.0;
      });

      // Gets the approximate stats at the current time by interpolating between closest points
      track.sampleLinear({ time: state.clock.time }, rawStatsPoint);

      // Creates the stat history for the the filtering if it doesnt exist or its changed
      if (this.statsFilters === undefined || !arraysEqual(this.filteredStatsList, Object.keys(rawStatsPoint))) {
        this.reCreateStatFilters(rawStatsPoint);
      }

      // Calls reset on all filter history if the player is paused. This is required as scrubbing
      // to a new location requires new filter history
      if (state.clock.playing === false) {
        this.filteredStatsList.forEach(statName => {
          this.statsFilters[statName].resetPoints();
        });
      }

      const smoothStatsPoint = {};

      // Ads the incoming data into the stats filters and plucks the newest filtered point
      this.filteredStatsList.forEach(statName => {
        this.statsFilters[statName].addPoint(rawStatsPoint[statName]);
        smoothStatsPoint[statName] = this.statsFilters[statName].newestPoint;
      });

      statsList.forEach(statName => {
        stats.push({
          id: statName,
          label: measures[statName].label,
          value: getStat(statName, smoothStatsPoint, [], state.stats.units, state),
          measure: getUnits(statName, state),
        });
      });
    }

    store.dispatch({
      type: 'SET_STATS',
      stats,
    });
  }

  render(store) {
    const state = store.getState();
    const statsList = state.stats.stats || [];

    const props = {
      statsArr: statsList,
      defaultStatsVisible: state.scene.defaultStatsVisible,
      numberVisible: Math.max(state.stats.numberVisible, state.scene.defaultStatsVisible),
      numberAvailable: statsList.length,
      toggleVisible: this.panelProps.toggleVisible,
    };

    const needsUpdate =
      props.statsArr.length > 0 || // @NOTE: Update every frame if we have stats
      props.defaultStatsVisible !== this.panelProps.defaultStatsVisible ||
      props.numberVisible !== this.panelProps.numberVisible ||
      props.numberAvailable !== this.panelProps.numberAvailable;

    const parent = document.getElementById('StatsPanelContainer');

    if (needsUpdate && parent) {
      ReactDOM.render(<StatsPanel {...props} />, parent);
      this.panelProps = props;
    }
  }
}

export function statsReducer(state = defaultStats, action) {
  switch (action.type) {
    case 'SET_UNITS':
      return {
        ...state,
        units: action.units,
        labels: unitTypes[action.units],
      };
    case 'VISIBLE_STATS_COUNT':
      return {
        ...state,
        numberVisible: action.numberVisible,
      };
    case 'SET_STATS':
      return {
        ...state,
        stats: action.stats,
      };
    case 'SET_VIEWER_OPTIONS':
      if (action.options.units) {
        return {
          ...state,
          units: action.options.units,
          labels: unitTypes[action.options.units],
        };
      } else {
        return state;
      }
    default:
      return state;
  }
}

function toggleFullStats(dispatch, count) {
  dispatch({
    type: 'VISIBLE_STATS_COUNT',
    numberVisible: count,
  });
}
